import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Target as TargetIcon, ChevronsDown, ChevronsUp, Bookmark, Building2, GripVertical, RotateCcw } from "lucide-react";
import { api } from "../api";
import { PageHead, Filters, Panel, LoadingBlock } from "../shell";
import { money, pct, growthClass, achievementSeverity, nodeTypeLabel, targetModeLabel } from "../lib/format";
import { can } from "../lib/permissions";
import { ScopeViewSwitch } from "../components/ScopeViewSwitch";

const DEFAULT_VIEW_KEY = "imc.targets.default_expanded_ids.v1";

export function TargetPerformance({ user, dataset, period, setPeriod, year, setYear, startDate, setStartDate, endDate, setEndDate, periodParams }) {
  const [viewScope, setViewScope] = useState("self");
  const [rootItems, setRootItems] = useState(null);
  const [childrenById, setChildrenById] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [savedNotice, setSavedNotice] = useState("");
  const [orderMeta, setOrderMeta] = useState({ source: "system", has_user_override: false });
  const [draggingId, setDraggingId] = useState(null);
  const [dropHint, setDropHint] = useState(null);
  const [orderBusy, setOrderBusy] = useState(false);
  const [loadingNodes, setLoadingNodes] = useState({});
  const canViewCompany = user?.target_performance_company_view === true;
  const companyReadOnly = viewScope === "company";
  const canReorder = !companyReadOnly && can(user,"targets.performance.reorder");
  const defaultViewKey = `${DEFAULT_VIEW_KEY}.${viewScope}`;
  const performanceLoadRequestRef = useRef(0);
  const draggingRef = useRef(null);
  const dropHintRef = useRef(null);
  const pointerCleanupRef = useRef(null);

  const fetchChildren = useCallback(async (parent_id) => {
    const r = await api.get("/analytics/target-performance", { params: { ...periodParams, parent_id, view_scope: viewScope } });
    return r.data.items;
  }, [periodParams, viewScope]);

  const reload = useCallback(async () => {
    const requestId = ++performanceLoadRequestRef.current;
    setLoading(true);
    let responses;
    try {
      responses = await Promise.all([
        api.get("/analytics/target-performance", { params: { ...periodParams, view_scope: viewScope } }),
        api.get("/analytics/target-performance/display-order", { params: { view_scope: viewScope } }),
      ]);
    } catch (error) {
      if (error.response?.status === 403 && viewScope === "company") setViewScope("self");
      else setSavedNotice(error.response?.data?.detail || "Hedef performansı yüklenemedi.");
      if (requestId === performanceLoadRequestRef.current) setLoading(false);
      return;
    }
    const [r, orderResponse] = responses;
    if (requestId !== performanceLoadRequestRef.current) return;
    setRootItems(r.data.items);
    setOrderMeta(orderResponse.data);
    setChildrenById({});
    // Restore saved default view if present
    let restore = {};
    try {
      const saved = JSON.parse(localStorage.getItem(defaultViewKey) || "[]");
      if (Array.isArray(saved) && saved.length) {
        const cache = {};
        for (const nid of saved) {
          try {
            const items = await fetchChildren(nid);
            cache[nid] = items; restore[nid] = true;
          } catch (e) { /* skip */ }
        }
        if (requestId === performanceLoadRequestRef.current) setChildrenById(cache);
      }
    } catch (e) { /* ignore */ }
    if (requestId === performanceLoadRequestRef.current) {
      setExpanded(restore);
      setLoading(false);
    }
  }, [periodParams, fetchChildren, viewScope, defaultViewKey]);

  // Primitive period fields intentionally trigger reload; periodParams is derived upstream.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [period, year, startDate, endDate, viewScope]);
  useEffect(() => { if (!canViewCompany && viewScope === "company") setViewScope("self"); }, [canViewCompany, viewScope]);

  const toggle = async (nodeId) => {
    if (expanded[nodeId]) {
      setExpanded((current) => {
        const next = { ...current };
        delete next[nodeId];
        return next;
      });
      return;
    }
    performanceLoadRequestRef.current += 1;
    setExpanded((current) => ({ ...current, [nodeId]: true }));
    if (childrenById[nodeId]) return;
    setLoadingNodes((current) => ({ ...current, [nodeId]: true }));
    try {
      const items = await fetchChildren(nodeId);
      setChildrenById((current) => ({ ...current, [nodeId]: items }));
    } catch (error) {
      setExpanded((current) => {
        const next = { ...current };
        delete next[nodeId];
        return next;
      });
      setSavedNotice(error.response?.data?.detail || "Alt kırılımlar yüklenemedi.");
      setTimeout(() => setSavedNotice(""), 2600);
    } finally {
      setLoadingNodes((current) => ({ ...current, [nodeId]: false }));
    }
  };

  // Expand all rows fully, walking recursively — bounded for safety
  const expandAll = async () => {
    performanceLoadRequestRef.current += 1;
    setLoading(true);
    const cache = { ...childrenById };
    const newExpanded = { ...expanded };
    const queue = [...(rootItems || []).filter((r) => r.has_children).map((r) => r.id)];
    let safety = 0;
    while (queue.length && safety < 500) {
      safety += 1;
      const id = queue.shift();
      newExpanded[id] = true;
      if (!cache[id]) {
        try { cache[id] = await fetchChildren(id); } catch (e) { cache[id] = []; }
      }
      for (const child of cache[id]) {
        if (child.has_children) queue.push(child.id);
      }
    }
    setChildrenById(cache);
    setExpanded(newExpanded);
    setLoading(false);
  };

  const collapseAll = () => setExpanded({});

  const saveDefaultView = () => {
    const ids = Object.keys(expanded).filter((k) => expanded[k]);
    localStorage.setItem(defaultViewKey, JSON.stringify(ids));
    setSavedNotice(`Varsayılan görünüm kaydedildi (${ids.length} düğüm).`);
    setTimeout(() => setSavedNotice(""), 2600);
  };

  const clearDefaultView = () => {
    localStorage.removeItem(defaultViewKey);
    setSavedNotice("Varsayılan görünüm sıfırlandı.");
    setTimeout(() => setSavedNotice(""), 2600);
  };

  const siblingsFor = (parentId) => parentId ? (childrenById[parentId] || []) : (rootItems || []);
  const setSiblings = (parentId, rows) => {
    if (parentId) setChildrenById((current) => ({ ...current, [parentId]: rows }));
    else setRootItems(rows);
  };
  const orderKey = (parentId) => parentId || "__root__";

  const persistSiblingOrder = async (parentId, rows, previousRows) => {
    setOrderBusy(true);
    try {
      await api.put("/analytics/target-performance/display-order", {
        scope: "user", orders: { [orderKey(parentId)]: rows.map((row) => row.id) },
      });
      setOrderMeta((current) => ({ ...current, source: "user", has_user_override: true }));
      setSavedNotice("Kişisel sıralamanız kaydedildi.");
    } catch (error) {
      setSiblings(parentId, previousRows);
      setSavedNotice(error.response?.data?.detail || "Sıralama kaydedilemedi.");
    } finally {
      setOrderBusy(false); setTimeout(() => setSavedNotice(""), 2400);
    }
  };

  const reorderSibling = async (source, target, position) => {
    if (!source || source.parent_id !== target.parent_id || source.id === target.id) return;
    performanceLoadRequestRef.current += 1;
    const previousRows = siblingsFor(source.parent_id);
    const withoutSource = previousRows.filter((row) => row.id !== source.id);
    let targetIndex = withoutSource.findIndex((row) => row.id === target.id);
    if (targetIndex < 0) return;
    if (position === "after") targetIndex += 1;
    const nextRows = [...withoutSource];
    nextRows.splice(targetIndex, 0, source);
    setSiblings(source.parent_id, nextRows);
    await persistSiblingOrder(source.parent_id, nextRows, previousRows);
  };

  const moveSibling = (row, direction) => {
    if (orderBusy) return;
    const siblings = siblingsFor(row.parent_id);
    const index = siblings.findIndex((item) => item.id === row.id);
    const target = siblings[index + direction];
    if (target) reorderSibling(row, target, direction < 0 ? "before" : "after");
  };

  const clearPointerDrag = () => {
    if (pointerCleanupRef.current) {
      const cleanup = pointerCleanupRef.current;
      pointerCleanupRef.current = null;
      cleanup();
    }
    document.body.classList.remove("target-pointer-dragging");
    draggingRef.current = null;
    dropHintRef.current = null;
    setDraggingId(null);
    setDropHint(null);
  };

  const beginPointerDrag = (event, row) => {
    if (orderBusy || event.button !== 0) return;
    event.preventDefault();
    clearPointerDrag();
    const pointerId = event.pointerId;
    draggingRef.current = row;
    setDraggingId(row.id);
    document.body.classList.add("target-pointer-dragging");

    const onMove = (pointerEvent) => {
      if (pointerEvent.pointerId !== pointerId) return;
      pointerEvent.preventDefault();
      const targetElement = document.elementFromPoint(pointerEvent.clientX, pointerEvent.clientY);
      const targetRow = targetElement?.closest("tr[data-order-node-id]");
      if (!targetRow || targetRow.dataset.orderParentId !== orderKey(row.parent_id)
          || targetRow.dataset.orderNodeId === row.id) {
        dropHintRef.current = null;
        setDropHint(null);
        return;
      }
      const rect = targetRow.getBoundingClientRect();
      const hint = { id: targetRow.dataset.orderNodeId,
                     position: pointerEvent.clientY < rect.top + rect.height / 2 ? "before" : "after" };
      dropHintRef.current = hint;
      setDropHint(hint);
    };
    const onUp = (pointerEvent) => {
      if (pointerEvent.pointerId !== pointerId) return;
      const hint = dropHintRef.current;
      if (hint) {
        const target = siblingsFor(row.parent_id).find((sibling) => sibling.id === hint.id);
        if (target) reorderSibling(row, target, hint.position);
      }
      clearPointerDrag();
    };
    const onCancel = (pointerEvent) => {
      if (pointerEvent.pointerId === pointerId) clearPointerDrag();
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    pointerCleanupRef.current = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  };

  const saveCompanyDefault = async () => {
    const orders = { __root__: (rootItems || []).map((row) => row.id) };
    Object.entries(childrenById).forEach(([parentId, rows]) => { orders[parentId] = rows.map((row) => row.id); });
    setOrderBusy(true);
    try {
      await api.put("/analytics/target-performance/display-order", { scope: "company_default", orders });
      setSavedNotice("Görünen sıralama şirket varsayılanı yapıldı.");
    } catch (error) { setSavedNotice(error.response?.data?.detail || "Şirket sırası kaydedilemedi."); }
    finally { setOrderBusy(false); setTimeout(() => setSavedNotice(""), 2600); }
  };

  const resetOrder = async (scope) => {
    setOrderBusy(true);
    try {
      await api.delete("/analytics/target-performance/display-order", { params: { scope } });
      setSavedNotice(scope === "user" ? "Şirket sıralamasına dönüldü." : "Şirket sırası sistem sırasına döndü.");
      await reload();
    } catch (error) { setSavedNotice(error.response?.data?.detail || "Sıralama sıfırlanamadı."); }
    finally { setOrderBusy(false); setTimeout(() => setSavedNotice(""), 2600); }
  };

  // Build a flat rendering list respecting expanded state
  const flatRows = useMemo(() => {
    const out = [];
    const walk = (rows, depth) => {
      (rows || []).forEach((r) => {
        out.push({ row: r, depth });
        if (expanded[r.id] && childrenById[r.id]) walk(childrenById[r.id], depth + 1);
      });
    };
    walk(rootItems, 0);
    return out;
  }, [rootItems, childrenById, expanded]);

  const RowRender = ({ row, depth }) => (
    <tr className={`perf-row depth-${Math.min(3, depth)} ${draggingId === row.id ? "is-dragging" : ""} ${dropHint?.id === row.id ? `drop-${dropHint.position}` : ""}`}
      data-testid={`target-row-${row.id}`}
      data-order-node-id={row.id}
      data-order-parent-id={orderKey(row.parent_id)}>
      <td>
        <div className="perf-label" style={{ paddingLeft: depth * 20 }}>
          {canReorder&&<button className="target-drag-handle" data-testid={`target-drag-handle-${row.id}`}
            type="button" aria-label={`${row.name} sırasını değiştir`}
            title="Sürükleyin veya klavyede yukarı/aşağı ok kullanın"
            onPointerDown={(event) => beginPointerDrag(event, row)}
            onKeyDown={(event) => {
              if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                event.preventDefault(); moveSibling(row, event.key === "ArrowUp" ? -1 : 1);
              }
            }}><GripVertical size={14}/></button>}
          {row.has_children ? (
            <button data-testid={`toggle-${row.id}`} className="icon-button" aria-expanded={Boolean(expanded[row.id])}
              aria-busy={Boolean(loadingNodes[row.id])} disabled={Boolean(loadingNodes[row.id])}
              onClick={() => toggle(row.id)}>
              {loadingNodes[row.id] ? <span className="inline-spinner" data-testid={`target-child-loading-${row.id}`}/> :
                expanded[row.id] ? <ChevronDown size={14}/> : <ChevronRight size={14}/>} 
            </button>
          ) : <span style={{ width: 22 }}/>}
          <strong>{row.name}</strong>
          <span className="tag" style={{ fontSize: 9 }}>{nodeTypeLabel(row.node_type)}</span>
          {row.target_mode !== "NONE" && row.target_mode !== "CHILD_SUM" && (
            <span className="tag" style={{ fontSize: 9, background: "#eef7f0", color: "#1f6a4a" }}>{targetModeLabel(row.target_mode)}</span>
          )}
        </div>
      </td>
      <td style={{textAlign:"right"}}>{money(row.annual_target, true)}</td>
      <td style={{textAlign:"right"}}>{money(row.period_target, true)}</td>
      <td style={{textAlign:"right"}}>{money(row.actual, true)}</td>
      <td>
        <div className="progress-cell">
          <div className={"progress " + achievementSeverity(row.achievement)}><span style={{ width: `${Math.min(100, row.achievement)}%` }}/></div>
          <span>{pct(row.achievement)}</span>
        </div>
      </td>
      <td style={{textAlign:"right"}}>{money(row.remaining, true)}</td>
      <td style={{textAlign:"right"}}>{money(row.previous, true)}</td>
      <td className={growthClass(row.growth)}>{row.growth !== null ? pct(row.growth) : "—"}</td>
      <td style={{textAlign:"right"}}>{money(row.forecast, true)}</td>
      <td><span className={"pill " + achievementSeverity(row.forecast_achievement)}>{pct(row.forecast_achievement)}</span></td>
    </tr>
  );

  return (
    <main className="content target-performance-page">
      <PageHead eyebrow="HEDEF · GERÇEKLEŞEN" title="Hedef Performansı"
        desc={"Organizasyon ağacında hedef ve tahmini gerçekleşme. Hedef her düğümde \"" + targetModeLabel("CHILD_SUM") + "\" veya \"" + targetModeLabel("EXPLICIT_TOTAL") + "\" moduna göre hesaplanır. Hedef = Komisyon Hedefi (Performans Hedef)."} />
      <Filters period={period} setPeriod={setPeriod} year={year} setYear={setYear}
               startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
               isDemo={dataset?.state?.active === "DEMO"}
               actions={<ScopeViewSwitch value={viewScope} onChange={setViewScope} canViewCompany={canViewCompany} testidPrefix="targets" />} />
      <div className="tree-toolbar" data-testid="targets-toolbar">
        <button data-testid="targets-expand-all" className="outline-button" onClick={expandAll}>
          <ChevronsDown size={14}/> Tümünü Aç
        </button>
        <button data-testid="targets-collapse-all" className="outline-button" onClick={collapseAll}>
          <ChevronsUp size={14}/> Tümünü Kapat
        </button>
        {!companyReadOnly && <button data-testid="targets-save-default" className="outline-button" onClick={saveDefaultView}>
          <Bookmark size={14}/> Varsayılan Görünümü Kaydet
        </button>}
        {!companyReadOnly && <button data-testid="targets-clear-default" className="text-button" onClick={clearDefaultView}>
          Açılımı Sıfırla
        </button>}
        <span className="target-order-source" data-testid="target-order-source">
          {orderMeta.source === "user" ? "Kişisel sıra" : orderMeta.source === "company_default" ? "Şirket sırası" : "Sistem sırası"}
        </span>
        {canReorder&&<button data-testid="target-order-reset-user-btn" className="outline-button" disabled={!orderMeta.has_user_override || orderBusy}
          onClick={() => resetOrder("user")}><RotateCcw size={13}/>Şirket Sırasına Dön</button>}
        {!companyReadOnly && can(user,"targets.company_order.manage") && <>
          <button data-testid="target-order-set-company-default-btn" className="outline-button" disabled={orderBusy}
            onClick={saveCompanyDefault}><Building2 size={13}/>Şirket Varsayılanı Yap</button>
          <button data-testid="target-order-reset-company-default-btn" className="text-button" disabled={orderBusy}
            onClick={() => resetOrder("company_default")}>Şirket Sırasını Sıfırla</button>
        </>}
        {companyReadOnly && <span className="tag" data-testid="targets-company-read-only-badge">Salt okunur şirket görünümü</span>}
        {savedNotice && <span className="save-notice" data-testid="targets-save-notice">{savedNotice}</span>}
      </div>
      {loading ? <LoadingBlock/> : (
        <div className="panel table-panel target-performance-table-wrap"
          data-testid="target-performance-scroll-container" role="region"
          aria-label="Hedef performansı yatay kaydırılabilir tablo" tabIndex={0}>
          <table data-testid="target-performance-table">
            <thead>
              <tr>
                <th>Organizasyon</th>
                <th style={{textAlign:"right"}}>Yıllık Komisyon Hedefi</th>
                <th style={{textAlign:"right"}}>Dönem Komisyon Hedefi</th>
                <th style={{textAlign:"right"}}>Gerçekleşen Komisyon</th>
                <th>Hedef %</th>
                <th style={{textAlign:"right"}}>Kalan</th>
                <th style={{textAlign:"right"}}>GY Aynı Dönem</th>
                <th>Büyüme</th>
                <th style={{textAlign:"right"}}>Yıl Sonu Tahmini</th>
                <th>Tahmini Hedef %</th>
              </tr>
            </thead>
            <tbody>{flatRows.map(({ row, depth }) => <RowRender key={row.id + "-" + depth} row={row} depth={depth}/>)}</tbody>
          </table>
        </div>
      )}
    </main>
  );
}

export function TargetEntry({ user }) {
  const [year, setYear] = useState(2026);
  const [sellers, setSellers] = useState([]);
  const [targets, setTargets] = useState([]);
  const [regionFilter, setRegionFilter] = useState("");
  const [form, setForm] = useState({ node_id: "", annual: "", distribution: "equal" });
  const [monthly, setMonthly] = useState(Array(12).fill(""));
  const [feedback, setFeedback] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [seasonalPreview, setSeasonalPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  // Inline-editing state
  const [editing, setEditing] = useState(null); // { nodeId, month }
  const [editValue, setEditValue] = useState("");
  const [monthOverrides, setMonthOverrides] = useState({}); // key `${nodeId}-${month}` => value
  const [rowSavingKey, setRowSavingKey] = useState(null);
  const [inlineError, setInlineError] = useState("");
  const [scopeInfo, setScopeInfo] = useState(null);
  const saveLockRef = useRef(false);
  const canEditTargets = can(user,"targets.entry.edit");
  const suppressBlurRef = useRef(false);
  const loadRequestRef = useRef(0);

  const load = async (requestedYear = year) => {
    const requestId = ++loadRequestRef.current;
    const response = await api.get("/targets", { params: { year: requestedYear } });
    if (requestId !== loadRequestRef.current) return;
    setTargets(response.data.items); setSellers(response.data.sellers); setScopeInfo(response.data);
  };
  useEffect(() => {
    setEditing(null); setMonthOverrides({}); setInlineError(""); setTargets([]); setSellers([]); setScopeInfo(null); load(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const canEditNode = (node) => node && node.target_mode !== "CHILD_SUM" && node.target_mode !== "NONE";
  const selectedNode = sellers.find((s) => s.id === form.node_id);

  // Auto-refresh seasonal preview when node/year/amount changes and mode = seasonal
  useEffect(() => {
    if (form.distribution !== "seasonal" || !form.node_id || !Number(form.annual)) {
      setSeasonalPreview(null); return;
    }
    setPreviewBusy(true); setSeasonalPreview(null);
    api.post("/targets/seasonal-preview", { node_id: form.node_id, year, annual_target: Number(form.annual) })
       .then((r) => setSeasonalPreview(r.data))
       .catch((e) => setSeasonalPreview({ error: e.response?.data?.detail || "Önizleme alınamadı" }))
       .finally(() => setPreviewBusy(false));
  }, [form.distribution, form.node_id, form.annual, year]);

  const submit = async () => {
    if (!form.node_id || !form.annual) { setFeedback({ ok: false, msg: "Organizasyon düğümü ve yıllık hedef zorunlu" }); return; }
    if (!canEditNode(selectedNode)) { setFeedback({ ok: false, msg: `${selectedNode.target_mode} modu manuel hedefe kapalı` }); return; }
    setBusy(true); setFeedback(null);
    try {
      const payload = {
        year, node_id: form.node_id,
        performance_target: Number(form.annual),
        distribution: form.distribution,
      };
      if (form.distribution === "manual") payload.monthly_values = monthly.map((v) => Number(v) || 0);
      const r = await api.post("/targets", payload);
      setFeedback({ ok: true, msg: `Komisyon Performans Hedefi kaydedildi (${r.data.items.length} aylık kayıt).` });
      setForm({ node_id: "", annual: "", distribution: "equal" }); setMonthly(Array(12).fill(""));
      load(year);
    } catch (e) { setFeedback({ ok: false, msg: e.response?.data?.detail || "Kaydedilemedi" }); }
    finally { setBusy(false); }
  };

  const months = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
  const regions = Array.from(new Set(sellers.map((s) => s.region_name).filter(Boolean))).sort();
  const filteredSellers = sellers.filter((s) => {
    if (regionFilter && s.region_name !== regionFilter) return false;
    if (search && !(s.seller_name.toUpperCase().includes(search.toUpperCase()) || (s.seller_code || "").toUpperCase().includes(search.toUpperCase()))) return false;
    return true;
  });
  const sellerRowsAll = filteredSellers.map((s) => {
    const monthlyRow = Array(12).fill(null);
    targets.filter((t) => t.node_id === s.id).forEach((t) => {
      if (t.month) monthlyRow[t.month - 1] = Number(t.performance_target);
    });
    // Apply local unsaved overrides
    let overriddenAny = false;
    const finalMonthly = monthlyRow.map((v, i) => {
      const k = `${s.id}-${i + 1}`;
      if (Object.prototype.hasOwnProperty.call(monthOverrides, k)) {
        overriddenAny = true;
        return Number(monthOverrides[k]);
      }
      return v;
    });
    const finalTotal = finalMonthly.reduce((sum, value) => sum + (value ?? 0), 0);
    const hasTargetRecord = finalMonthly.some((value) => value !== null);
    return { seller: s, monthly: finalMonthly, total: finalTotal,
             dirty: overriddenAny, hasTargetRecord,
             visibleFromTemplate: Boolean(s.entry_template) };
  });

  // "Hedefsizleri göster" toggle — hide NONE nodes with no monthly targets by default.
  // Search overrides the toggle (a match is always shown).
  const searchActive = !!search.trim();
  const sellerRows = sellerRowsAll.filter((row) => {
    if (showEmpty || searchActive) return true;
    return row.hasTargetRecord || row.visibleFromTemplate;
  });

  const hiddenCount = sellerRowsAll.length - sellerRows.length;

  const canInlineEdit = (mode) => mode === "EXPLICIT_TOTAL" || mode === "OWN_PLUS_CHILDREN";

  const beginEdit = (nodeId, monthIdx, currentValue) => {
    setInlineError("");
    setEditing({ nodeId, month: monthIdx + 1 });
    setEditValue(currentValue !== null && currentValue !== undefined ? String(currentValue).replace(".", ",") : "");
  };

  const cancelEdit = () => { setEditing(null); setEditValue(""); setInlineError(""); };

  const parseTurkishNumber = (value) => {
    const text = (value ?? "").toString().replace(/\s/g, "").replace(/₺|TL/gi, "");
    if (!text) return null;
    if (text.includes(",")) return Number(text.replace(/\./g, "").replace(",", "."));
    const dottedThousands = /^\d{1,3}(\.\d{3})+$/.test(text);
    return Number(dottedThousands ? text.replace(/\./g, "") : text);
  };

  const adjacentEditableCell = (nodeId, month, direction = 1) => {
    const cells = sellerRows.flatMap((row) => canInlineEdit(row.seller.target_mode)
      ? months.map((_, index) => ({ nodeId: row.seller.id, month: index + 1, value: row.monthly[index] }))
      : []);
    const current = cells.findIndex((cell) => cell.nodeId === nodeId && cell.month === month);
    return cells[current + direction] || null;
  };

  const commitEdit = async (direction = 0) => {
    if (!editing || saveLockRef.current) return;
    const num = parseTurkishNumber(editValue);
    if (num === null) {
      setInlineError("Boş değer kabul edilmez"); return;
    }
    if (Number.isNaN(num) || num < 0) {
      setInlineError("Geçersiz veya negatif değer"); return;
    }
    const { nodeId, month } = editing;
    const key = `${nodeId}-${month}`;
    saveLockRef.current = true;
    setRowSavingKey(key);
    try {
      const r = await api.patch(`/targets/${nodeId}/month/${month}`, {
        year, performance_target: num,
      });
      // Reflect the persisted value locally without a full reload
      setMonthOverrides((prev) => ({ ...prev, [key]: r.data.item.performance_target }));
      setInlineError("");
      const next = direction ? adjacentEditableCell(nodeId, month, direction) : null;
      if (next) {
        setEditing({ nodeId: next.nodeId, month: next.month });
        setEditValue(next.value !== null && next.value !== undefined
          ? String(next.value).replace(".", ",") : "");
      } else {
        setEditing(null); setEditValue("");
      }
    } catch (e) {
      setInlineError(e.response?.data?.detail || "Kaydedilemedi");
    } finally {
      saveLockRef.current = false;
      setRowSavingKey(null);
    }
  };

  const onEditKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); suppressBlurRef.current = true; commitEdit(e.shiftKey ? -1 : 1);
    }
    else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
    else if (e.key === "Tab") {
      e.preventDefault(); suppressBlurRef.current = true; commitEdit(e.shiftKey ? -1 : 1);
    }
  };

  return (
    <main className="content target-entry-page">
      <PageHead eyebrow="HEDEFLER · GİRİŞ" title="Komisyon Performans Hedefi Girişi"
        desc="Gerçek IMC organizasyon ağacı ve içeri aktarılan Performans Hedef değerleri. Hedef modu 'Alt Hedefleri Topla' olan düğümler otomatik hesaplanır (giriş kapalıdır)." />
      {scopeInfo && (
        <div className="scope-reconciliation-banner" data-testid="target-scope-reconciliation-banner">
          <strong data-testid="target-scope-year">{year} hedef kapsamı</strong>
          <span>{scopeInfo.scope === "historical"
            ? "Geçmiş dönem: pasif kayıtlar geriye dönük dışlanmaz."
            : "Bugün/gelecek: pasif kayıtlar hedef toplamına katılmaz."}</span>
          <span data-testid="target-scope-total">Toplam {money(scopeInfo.target_total, false)}</span>
          {scopeInfo.excluded_inactive_count > 0 && (
            <span data-testid="target-scope-excluded-count">{scopeInfo.excluded_inactive_count} pasif kayıt kapsam dışında</span>
          )}
        </div>
      )}
      <div className="filters">
        <div className="filter-label">Hedef yılı</div>
        <select data-testid="target-year-select" className="year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[2026, 2025, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="filter-divider" />
        <select data-testid="target-region-filter" value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}>
          <option value="">Tüm bölgeler</option>
          {regions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <div className="searchbox" style={{ margin: 0 }}>
          <input data-testid="target-search" placeholder="Satıcı ara" value={search} onChange={(e) => setSearch(e.target.value)}/>
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, marginLeft: 8 }}>
          <input data-testid="target-show-empty-toggle" type="checkbox" checked={showEmpty}
                  onChange={(e) => setShowEmpty(e.target.checked)}/>
          Hedefsizleri göster
        </label>
        <span className="filter-spacer" />
        <span className="tag" data-testid="target-count">
          {sellerRows.length} / {sellerRowsAll.length} satıcı · {targets.length} aylık hedef
          {!showEmpty && !searchActive && hiddenCount > 0 && (
            <> · <span data-testid="target-hidden-count">{hiddenCount} gizli</span></>
          )}
        </span>
      </div>
      <section className="lower-grid target-entry-grid">
        <Panel title={`${year} yılı Komisyon Performans Hedefi tablosu`} sub="Aylık dağılım — gerçek Excel Performans Hedef verisi">
          <div className="table-panel target-matrix-wrap" style={{ maxHeight: 520, overflow: "auto" }}>
            <table className="target-matrix" data-testid="target-matrix-table">
              <thead>
                <tr>
                  <th>Satıcı (Bölge · Ekip)</th>
                  <th>Hedef Modu</th>
                  {months.map((m) => <th key={m} style={{textAlign:"right"}}>{m}</th>)}
                  <th style={{textAlign:"right"}}>Yıllık Toplam</th>
                </tr>
              </thead>
              <tbody>
                {sellerRows.map((row) => {
                  const editable = canEditTargets && canInlineEdit(row.seller.target_mode);
                  return (
                  <tr key={row.seller.id} data-testid={`target-row-${row.seller.id}`}
                      className={(editable ? "" : "row-disabled ") + (row.dirty ? "row-dirty" : "")}>
                    <td className="target-node-cell">
                      <strong>{row.seller.seller_name}</strong>
                      {row.seller.node_type && row.seller.node_type !== "SELLER" && (
                        <span className="tag" style={{ fontSize: 9, marginLeft: 6, background: "#eef2ff", color: "#3730a3" }}
                              data-testid={`target-nodetype-${row.seller.id}`}>{
                          row.seller.node_type === "SUBGROUP" ? "Alt Grup" :
                          row.seller.node_type === "GROUP" ? "Grup" :
                          row.seller.node_type === "TEAM" ? "Ekip" :
                          row.seller.node_type === "CHANNEL" ? "Kanal" :
                          row.seller.node_type
                        }</span>
                      )}
                      {row.dirty && <span className="tag" data-testid={`target-dirty-${row.seller.id}`}
                                          style={{ fontSize: 9, marginLeft: 6, background: "#fff7ed", color: "#9a3412" }}>Kaydedildi</span>}
                      <div className="muted" style={{ fontSize: 10 }}>{row.seller.region_name}{row.seller.team_name ? ` · ${row.seller.team_name}` : ""}</div>
                    </td>
                    <td className="target-mode-cell"><span className="tag" style={{ fontSize: 9 }}>{row.seller.target_mode}</span></td>
                    {row.monthly.map((m, i) => {
                      const isEditing = editing && editing.nodeId === row.seller.id && editing.month === i + 1;
                      const key = `${row.seller.id}-${i + 1}`;
                      const saving = rowSavingKey === key;
                      if (isEditing) {
                        return (
                          <td key={i} className="target-month-cell" data-month-label={months[i]} style={{ textAlign: "right", padding: 2 }}>
                            <input
                              data-testid={`target-month-edit-${row.seller.id}-${i + 1}`}
                              autoFocus
                              type="text"
                              inputMode="decimal"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={onEditKey}
                              onBlur={() => {
                                if (suppressBlurRef.current) { suppressBlurRef.current = false; return; }
                                commitEdit(0);
                              }}
                              disabled={saving}
                              style={{ width: "100%", textAlign: "right", fontSize: 11, padding: "2px 4px" }}
                            />
                            {inlineError && (
                              <div className="error" data-testid="target-inline-error"
                                    style={{ fontSize: 9, marginTop: 2 }}>{inlineError}</div>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td
                          key={i}
                          data-testid={`target-month-cell-${row.seller.id}-${i + 1}`}
                          data-month-label={months[i]}
                          className="target-month-cell"
                          style={{
                            textAlign: "right",
                            cursor: editable ? "pointer" : "default",
                            background: editable ? undefined : "transparent",
                          }}
                          title={editable ? "Tıklayın: bu ayın hedefini düzenle" : `${row.seller.target_mode} modu düzenlemeye kapalı`}
                          onClick={() => { if (editable) beginEdit(row.seller.id, i, m); }}
                        >
                          {m !== null ? money(m, true) : "—"}
                        </td>
                      );
                    })}
                    <td className="target-total-cell" style={{textAlign:"right"}} data-testid={`target-annual-total-${row.seller.id}`}>
                      <strong>{money(row.total, true)}</strong>
                    </td>
                  </tr>
                  );
                })}
                {sellerRows.length === 0 && <tr><td colSpan={15} className="empty-state">Kayıt yok.</td></tr>}
              </tbody>
            </table>
          </div>
        </Panel>
        {canEditTargets?<Panel title="Yeni Komisyon Performans Hedefi Gir" sub="Sadece EXPLICIT_TOTAL / OWN_PLUS_CHILDREN modundaki düğümler">
          <div className="form-grid">
            <label>Organizasyon Düğümü (Satıcı){selectedNode && !canEditNode(selectedNode) && <span className="muted" style={{ fontSize: 10, marginLeft: 6 }}>{selectedNode.target_mode} — düzenlenemez</span>}
              <select data-testid="target-seller-select" value={form.node_id} onChange={(e) => setForm({ ...form, node_id: e.target.value })}>
                <option value="">Seçin</option>
                {sellers.map((s) => <option key={s.id} value={s.id} disabled={!canEditNode(s)}>
                  {`${s.seller_name} — ${s.region_name}${!canEditNode(s) ? ` (${s.target_mode})` : ""}`}
                </option>)}
              </select>
            </label>
            <label>Yıllık Komisyon Performans Hedefi (TL)
              <input data-testid="target-annual-input" type="number" step="1000" value={form.annual} onChange={(e) => setForm({ ...form, annual: e.target.value })}/>
            </label>
            <label>Dağıtım
              <select data-testid="target-distribution-select" value={form.distribution} onChange={(e) => setForm({ ...form, distribution: e.target.value })}>
                <option value="equal">12 aya eşit dağıt</option>
                <option value="seasonal">Geçen yıl sezonsallığı</option>
                <option value="manual">Manuel dağıt</option>
              </select>
            </label>
            {form.distribution === "manual" && (
              <div className="monthly-grid">
                {months.map((m, i) => (
                  <label key={m}>{m}<input data-testid={`target-month-${i+1}`} type="number" value={monthly[i]} onChange={(e) => {
                    const c = [...monthly]; c[i] = e.target.value; setMonthly(c);
                  }}/></label>
                ))}
              </div>
            )}
            {form.distribution === "seasonal" && (
              <div data-testid="seasonal-preview" style={{ background: "#f8fafc", border: "1px solid var(--line)", borderRadius: 3, padding: 8 }}>
                <div className="muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: .4, marginBottom: 6 }}>
                  Geçen yıl sezonsallığı önizlemesi
                </div>
                {previewBusy && <div className="muted" style={{ fontSize: 11 }}>Hesaplanıyor…</div>}
                {seasonalPreview?.error && <div className="error" data-testid="seasonal-preview-error">{seasonalPreview.error}</div>}
                {seasonalPreview && !seasonalPreview.error && (
                  <>
                    {seasonalPreview.notice && <div className="save-notice" data-testid="seasonal-notice" style={{ marginBottom: 6 }}>{seasonalPreview.notice}</div>}
                    <div className="muted" style={{ fontSize: 10, marginBottom: 6 }} data-testid="seasonal-source">
                      Kaynak: {seasonalPreview.source === "SELF" ? "Kendi geçen yıl gerçekleşmesi"
                              : seasonalPreview.source?.startsWith("PARENT") ? `Üst düğüm (${seasonalPreview.source_node_name}) geçen yıl gerçekleşmesi`
                              : seasonalPreview.source === "OVERALL" ? "Genel IMC geçen yıl gerçekleşmesi"
                              : seasonalPreview.source === "EQUAL" ? "Eşit dağıtım (fallback)" : seasonalPreview.source}
                      {" · Baz yıl: " + seasonalPreview.prev_year}
                    </div>
                    <table style={{ width: "100%", fontSize: 11 }} data-testid="seasonal-preview-table">
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left" }}>Ay</th>
                          <th style={{ textAlign: "right" }}>Geçen Yıl Komisyonu (TL)</th>
                          <th style={{ textAlign: "right" }}>Sezonsallık %</th>
                          <th style={{ textAlign: "right" }}>Yeni Hedef (TL)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {seasonalPreview.months.map((mm) => (
                          <tr key={mm.month} data-testid={`seasonal-row-${mm.month}`}>
                            <td>{months[mm.month - 1]}</td>
                            <td style={{ textAlign: "right" }}>{money(mm.prev_commission, true)}</td>
                            <td style={{ textAlign: "right" }}>%{(mm.share * 100).toFixed(2).replace(".", ",")}</td>
                            <td style={{ textAlign: "right" }}><strong>{money(mm.target, true)}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={3} style={{ textAlign: "right", fontWeight: 600 }}>Toplam = Yıllık Komisyon Performans Hedefi</td>
                          <td style={{ textAlign: "right", fontWeight: 700 }} data-testid="seasonal-preview-total">{money(seasonalPreview.sum_check, true)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </>
                )}
              </div>
            )}
            <button data-testid="target-save-button" className="primary-button" onClick={submit}
                    disabled={busy || !canEditNode(selectedNode)}>
              {busy ? "Kaydediliyor..." : "Hedef kaydet"} <TargetIcon size={15}/>
            </button>
            {feedback && <div className={feedback.ok ? "success-box" : "error"} data-testid="target-feedback">{feedback.msg}</div>}
          </div>
        </Panel>:<Panel title="Hedef Girişi" sub="Bu rol için hedef giriş yetkisi kapalı"><div data-testid="target-entry-readonly" className="empty-state">Mevcut hedefleri görüntüleyebilirsiniz.</div></Panel>}
      </section>
    </main>
  );
}
